import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TrendUp, Warning, Clock, CheckCircle } from "@phosphor-icons/react";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StageBadge } from "@/components/common";
import Analytics from "@/components/Analytics";
import OnboardingChecklist from "@/components/OnboardingChecklist";

const TONES = {
  primary: "border-l-primary",
  neutral: "border-l-slate-400",
  warning: "border-l-gold",
  alert: "border-l-alert",
};
const TONE_ICON = { primary: TrendUp, neutral: CheckCircle, warning: Clock, alert: Warning };

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-muted-foreground">Loading dashboard…</div>;

  const lists = data.lists || {};

  return (
    <div>
      <PageHeader overline={`${user?.role_label} · Dashboard`} title={`Good day, ${user?.name?.split(" ")[0]}`}>
        Your role-specific snapshot of records awaiting action, due-soon items, and portfolio funding.
      </PageHeader>

      <OnboardingChecklist />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {data.cards.map((c, i) => {
          const Icon = TONE_ICON[c.tone] || TrendUp;
          return (
            <div
              key={i}
              data-testid={`stat-card-${i}`}
              className={`bg-card border border-border border-l-4 ${TONES[c.tone]} rounded-md p-5 rise`}
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="flex items-start justify-between">
                <div className="overline text-muted-foreground">{c.label}</div>
                <Icon size={18} className="text-muted-foreground" />
              </div>
              <div className="font-head font-bold text-3xl tracking-tight mt-3">
                {c.money ? money(c.value) : c.value}
              </div>
            </div>
          );
        })}
      </div>

      {["leadership", "admin"].includes(user?.role) && <Analytics />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {lists.queue && (
          <ListPanel title="Awaiting Your Review" rows={lists.queue} navigate={navigate} empty="Your review queue is clear." />
        )}
        {lists.returned && (
          <ListPanel title="Returned for Revision" rows={lists.returned} navigate={navigate} empty="Nothing returned." />
        )}
        {lists.nearing_closeout && (
          <ListPanel title="Nearing Closeout (30 days)" rows={lists.nearing_closeout} navigate={navigate} empty="No closeouts due soon." />
        )}
        {lists.overdue && (
          <ListPanel title="Overdue Reviews" rows={lists.overdue} navigate={navigate} empty="No overdue reviews." />
        )}
      </div>
    </div>
  );
}

function ListPanel({ title, rows, navigate, empty }) {
  return (
    <div className="bg-card border border-border rounded-md">
      <div className="px-5 py-3.5 border-b border-border font-head font-semibold text-sm">{title}</div>
      {rows.length === 0 ? (
        <div className="px-5 py-8 text-sm text-muted-foreground text-center">{empty}</div>
      ) : (
        <div className="divide-y divide-border">
          {rows.map((c) => (
            <button
              key={c.id}
              data-testid={`dash-row-${c.id}`}
              onClick={() => navigate(`/contracts/${c.id}`)}
              className="w-full flex items-center justify-between px-5 py-3 hover:bg-secondary/60 transition-colors text-left"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{c.title}</div>
                <div className="text-xs text-muted-foreground">{c.contract_number}</div>
              </div>
              <StageBadge stage={c.workflow_stage} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
